import { Base64 } from 'js-base64';
import { FORM_TYPE_CREATE_REUSABLE_TX } from 'js/constants/formTypes';
import { SUPPORT_KEY_REUSABLE_TX } from 'js/constants/supportPageKeys';
import {
  convertCodeToTransform,
  encodeTransformCode,
  getCodeForReusableRecordTx,
} from 'js/lib/TransformsUtils';
import forEach from 'lodash/forEach';
import has from 'lodash/has';
import map from 'lodash/map';
import trim from 'lodash/trim';
import uniq from 'lodash/uniq';
import ReusableTransform from 'new/components/ReusableTransform';
import { Texts } from 'new/components/ReusableTransform/resources';
import { TransformType } from 'new/components/ReusableTransform/types';
import React, { Component } from 'react';
import Constants from '../../Constants';
import { ACLQueryModeTypes } from '../../constants/visibilityFilters';
import JSONUtils from '../../lib/JSONUtils';
import '../data_maps/DataMapDashboard.scss';
import Form from '../elements/forms/Form';
import FormPage from '../elements/forms/FormPage';
import EditCustomProcessor from './EditCustomProcessor';
import './form/CreateTxForm.scss';

const displayNameByTransformType = {
  [TransformType.ATTRIBUTE]: 'Attribute Transform',
  [TransformType.RECORD]: 'Record Transform',
  [TransformType.CUSTOM]: 'Connector Processor',
};

class CreateReusableTxForm extends Component {
  constructor(props) {
    super(props);
    this.state = {
      contentHeight: 800,
      awaitingTxResponse: false,
    };

    this._validateAttrTx = this._validateAttrTx.bind(this);
    this._validateRecordTx = this._validateRecordTx.bind(this);
    this._validateCustomProcessor = this._validateCustomProcessor.bind(this);
  }

  _loadReusableTransforms = () => {
    if (!this.props.isLoadingReusableTransforms) {
      this.props.getAllReusableTransforms(ACLQueryModeTypes.MODE_COLLABORATOR.key);
    }
  };

  componentDidMount() {
    this.props.setContextualSupport(SUPPORT_KEY_REUSABLE_TX);

    if (this.props.formData.shouldInitFormData === true) {
      this.props.updateFormData(FORM_TYPE_CREATE_REUSABLE_TX, {
        ...this.props.formData,
        shouldInitFormData: false,
      });
    }

    this._loadReusableTransforms();
  }

  componentWillUnmount() {
    this.props.unsetContextualSupport(SUPPORT_KEY_REUSABLE_TX);
  }

  componentDidUpdate() {
    const {
      formData: { txUpdateDone },
    } = this.props;
    const { awaitingTxResponse } = this.state;

    if (awaitingTxResponse && txUpdateDone) {
      this._boundValidateTx();
    }
  }

  _handleEditTxClicked = async (tx) => {
    this.props.onFormLoadButtonClicked(tx);
    this.props.updateFormData(FORM_TYPE_CREATE_REUSABLE_TX, {
      shouldInitFormData: true,
    });
  };

  _handleSubmit = () => {
    this.props.onFormSubmitted();
  };

  _handleDismiss = () => {
    this.props.onFormDismissed();
  };

  _getTxPayload = (processSamples = false, isTestMode = false) => {
    const {
      formData: { input, name, description, code, codeLanguage, txArgs, transformType },
    } = this.props;

    if (transformType === TransformType.RECORD) {
      let txPayloadCode = null;

      try {
        const txCode = convertCodeToTransform(code, codeLanguage);
        txPayloadCode = getCodeForReusableRecordTx(txCode);
      } catch (e) {
        console.error('An error at _getTxPayload:', e);
      }

      let inputSamples = null;
      if (processSamples === true) {
        inputSamples = JSONUtils.parseJson(input, true);
      }

      return { txPayloadCode, inputSamples };
    }

    //create attrTxObject from payload
    let encodedCode = encodeTransformCode(code);
    const attrTxObject = {
      name: isTestMode ? `Draft - ${name ? name : ''}` : name,
      description,
      reusable: true,
      code_type: codeLanguage,
      output_type: TransformType.ATTRIBUTE,
      code_encoding: 'base64',
      code: encodedCode,
      custom_config: {
        ui: {
          txArgs,
        },
      },
    };

    let inputSample = null;
    if (processSamples === true) {
      inputSample = JSONUtils.parseJson(input, true);
    }

    return { attrTxObject, inputSample };
  };

  _handleCreateNewDocumentation(response, text) {
    if (!response || !text) return;
    const { updateTransformDoc } = this.props;
    const [_transformID] = Object.keys(response?.entities?.transforms) || [];
    updateTransformDoc(_transformID, text);
  }

  _txNameIsValid = () => {
    const { formData } = this.props;
    return typeof formData?.name === 'string' && formData.name.trim();
  };

  _validateAttrTx(data, onSuccess, onError) {
    const { formData } = this.props;
    const { awaitingTxResponse } = this.state;

    if (!this._txNameIsValid()) {
      onError('Please set a name for this Transform');
      return;
    }

    //Test function arguments
    if (has(formData, 'txArgs')) {
      let items = formData.txArgs.items;
      let paramNames = map(items, (p) => p.name);
      //all params need to be unique non blank names
      let uniqueParamNames = uniq(paramNames);

      if (uniqueParamNames.length !== paramNames.length) {
        return onError('Argument names must be unique');
      }

      let hasBlankNames = false;
      forEach(paramNames, (p) => {
        if (trim(p) === '') {
          hasBlankNames = true;
          return false;
        }
      });
      if (hasBlankNames) {
        return onError('Argument names cannot be blank');
      }
    }

    if (awaitingTxResponse) {
      if (has(formData, 'txUpdateError') && formData.txUpdateError) {
        this.setState({
          awaitingTxResponse: false,
        });

        setTimeout(() => {
          this.props.updateFormData(FORM_TYPE_CREATE_REUSABLE_TX, {
            txUpdateDone: false,
            txUpdateError: null,
          });
        }, 100);
        onError(formData.txUpdateError.message);
        return;
      }

      this.setState({
        awaitingTxResponse: false,
      });
      onSuccess();
      return;
    }
    this._boundValidateTx = this._validateAttrTx.bind(this, ...arguments);

    this.setState({ awaitingTxResponse: true }, async () => {
      // create a new transform, or update existing one
      const { isEditMode } = this.props;
      let { attrTxObject } = this._getTxPayload(false, false);

      if (!isEditMode) {
        const { documentation = '' } = formData;
        const { createAttrTxFromForm } = this.props;
        const response = await createAttrTxFromForm(FORM_TYPE_CREATE_REUSABLE_TX, attrTxObject);

        this._handleCreateNewDocumentation(response, documentation);
      } else {
        this.props.updateAttrTxFromForm(
          FORM_TYPE_CREATE_REUSABLE_TX,
          this.props.transformBeingEdited.id,
          attrTxObject,
        );
      }
    });
  }

  _validateRecordTx(data, onSuccess, onError) {
    const { formData } = this.props;
    const { awaitingTxResponse } = this.state;

    if (!this._txNameIsValid()) {
      onError('Please set a name for this Transform');
      return;
    }

    if (awaitingTxResponse) {
      if (has(formData, 'txUpdateError') && formData.txUpdateError) {
        this.setState({ awaitingTxResponse: false });

        setTimeout(() => {
          this.props.updateFormData(FORM_TYPE_CREATE_REUSABLE_TX, {
            txUpdateDone: false,
            txUpdateError: null,
          });
        }, 100);
        onError(formData.txUpdateError.message);
        return;
      }

      this.setState({
        awaitingTxResponse: false,
      });
      onSuccess();
      return;
    }

    this._boundValidateTx = this._validateRecordTx.bind(this, ...arguments);

    this.setState({ awaitingTxResponse: true }, async () => {
      //create a new transform, or update existing one
      const { isEditMode, formData } = this.props;
      const { name, description, code, codeLanguage } = formData;

      let txCode = null;
      try {
        txCode = convertCodeToTransform(code, codeLanguage);
      } catch (error) {
        console.error('Error at convertCodeToTransform function: ', error);
      }

      if (txCode === null) {
        this.setState({ awaitingTxResponse: false });
        return;
      }

      const recordTxObject =
        isEditMode === true
          ? {
              name: name ? name : '',
              description: description ? description : '',
              code: txCode,
              reusable: true,
            }
          : {
              name: name ? name : '',
              description: description ? description : '',
              output_type: TransformType.RECORD,
              code_type: 'jolt_custom',
              code_encoding: 'none',
              code: txCode,
              reusable: true,
            };

      if (isEditMode === false) {
        const { documentation = '' } = formData;
        const { createReusableRecordTxFromForm } = this.props;

        const response = await createReusableRecordTxFromForm(
          FORM_TYPE_CREATE_REUSABLE_TX,
          recordTxObject,
        );

        this._handleCreateNewDocumentation(response, documentation);
      } else {
        this.props.updateReusableRecordTxFromForm(
          FORM_TYPE_CREATE_REUSABLE_TX,
          this.props.transformBeingEdited.id,
          recordTxObject,
        );
      }
    });
  }

  async _validateCustomProcessor(data, onSuccess, onError) {
    const { formData, transformBeingEdited, createCodeContainer, updateCodeContainer } = this.props;
    const { awaitingTxResponse } = this.state;
    const {
      name,
      description,
      documentation = '',
      path,
      customProcessorCode,
      customProcessorConfig,
    } = formData;

    if (!this._txNameIsValid()) {
      onError('Please set a name for this Transform');
      return;
    }

    let config;

    try {
      config = JSON.parse(customProcessorConfig);
    } catch (e) {
      onError('The configuration is malformed. Please check that it is a valid JSON object.');
      return;
    }

    const payload = {
      name,
      description,
      resource_type: 'source_custom',
      output_type: TransformType.CUSTOM,
      code_type: 'python',
      code_encoding: 'base64',
      code: Base64.encode(customProcessorCode),
      code_config: config?.code_container_config || {},
      reusable: true,
    };

    try {
      if (transformBeingEdited) {
        await updateCodeContainer(transformBeingEdited.id, payload);
      } else {
        const response = await createCodeContainer(payload);
        this._handleCreateNewDocumentation(response, documentation);
      }
    } catch (e) {
      onError('Failed to save custom processor');
    }

    onSuccess();
  }

  _handleTransformTestTriggered = () => {
    const { formData, testAttributeTransform, testReusableRecordTransform } = this.props;

    const { transformType, txArgsTestData } = formData;

    if (!Object.values(TransformType).includes(transformType)) return;

    if (transformType === TransformType.RECORD) {
      const { txPayloadCode, inputSamples } = this._getTxPayload(true);

      if (txPayloadCode) {
        testReusableRecordTransform(txPayloadCode, inputSamples, txArgsTestData);
      }
      return;
    }

    /**
     * TransformType.ATTRIBUTE
     */
    const { attrTxObject, inputSample } = this._getTxPayload(true, true);
    testAttributeTransform({
      inputSample,
      tx: attrTxObject,
      inputArgumentsTestData: txArgsTestData,
    });
  };

  _onFormContentResized = (contentHeight = 800) => {
    this.setState({
      contentHeight: contentHeight,
    });
  };

  render() {
    const { isEditMode, transformBeingEdited, formData, ...otherProps } = this.props;
    const { preSelectedTransformType } = otherProps;
    const { transformType } = formData;
    const actionName = isEditMode ? 'Edit' : 'Create';
    const title = `${actionName} ${displayNameByTransformType[transformType] || 'Transform'}`;
    const subtitle =
      isEditMode && transformBeingEdited
        ? `${transformBeingEdited.name} (ID: ${transformBeingEdited.id})`
        : '';

    const submitButtonText = isEditMode ? 'Save' : 'Create';
    const validationMethod =
      transformType === TransformType.RECORD
        ? this._validateRecordTx
        : transformType === TransformType.CUSTOM
          ? this._validateCustomProcessor
          : this._validateAttrTx;

    return (
      <Form
        title={title}
        type={FORM_TYPE_CREATE_REUSABLE_TX}
        subtitle={subtitle}
        onSubmit={this._handleSubmit}
        onDismiss={this._handleDismiss}
        submitButtonText={submitButtonText}
        alwaysShowCancel
        onFormContentResized={this._onFormContentResized}
        promptTitle={Texts.promptTitle}
        promptMessage={Texts.promptMessage}
        promptCancelBtnLabel={Texts.promptCancelButton}
        promptConfirmBtnLabel={Texts.promptConfirmButton}
      >
        <FormPage
          title=""
          contentHeight={Constants.SCHEMA_EDITOR_FORM_HEIGHT}
          validationMethod={validationMethod}
        >
          {[preSelectedTransformType, transformBeingEdited?.output_type].includes(
            TransformType.CUSTOM,
          ) ? (
            <EditCustomProcessor transformBeingEdited={transformBeingEdited} />
          ) : (
            <ReusableTransform
              {...otherProps}
              isEditMode={isEditMode}
              transformBeingEditedID={transformBeingEdited?.id}
              onClickDismiss={this._handleDismiss}
              runTransform={this._handleTransformTestTriggered}
              onEditButtonClicked={this._handleEditTxClicked}
            />
          )}
        </FormPage>
      </Form>
    );
  }
}

CreateReusableTxForm.defaultProps = {
  isEditMode: false,
  transformBeingEdited: null,
};

export default CreateReusableTxForm;
